import React, { useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Popconfirm, Spin, Table, Tag, Typography, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';

const { Text } = Typography;

/**
 * Allowlist of regulator hosts the AI curator may fetch source text from (SSRF guard: a URL whose
 * host is not here is refused server-side, so nothing else is ever fetched). platform_admin only.
 * Removing a host is a soft delete - the row stays as the audit trail.
 */
const SourceDomainManager = () => {
    const { request } = useApiRequest();
    const [rev, setRev] = useState(0);
    const [hostname, setHostname] = useState('');
    const [label, setLabel] = useState('');
    const [includeSubdomains, setIncludeSubdomains] = useState(false);
    const [busy, setBusy] = useState(false);
    const [formError, setFormError] = useState(null);

    const domains = useResource(
        async () => (await request('GET', '/admin/question-bank/source-domains')).data.domains || [],
        [request, rev],
    );

    const add = async () => {
        if (!hostname.trim() || !label.trim()) {
            setFormError('Enter both a host name (for example www.meity.gov.in) and a label.');
            return;
        }
        setBusy(true);
        setFormError(null);
        try {
            await request('POST', '/admin/question-bank/source-domains', {
                body: { hostname: hostname.trim(), label: label.trim(), includeSubdomains },
            });
            message.success(`${hostname.trim()} added to the approved list.`);
            setHostname('');
            setLabel('');
            setIncludeSubdomains(false);
            setRev((r) => r + 1);
        } catch (err) {
            setFormError(userMessageForError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (row) => {
        try {
            await request('DELETE', `/admin/question-bank/source-domains/${row.id}`);
            message.success(`${row.hostname} removed from the approved list.`);
        } catch (err) {
            message.error(userMessageForError(err));
        } finally {
            setRev((r) => r + 1);
        }
    };

    const columns = [
        { title: 'Host', key: 'hostname', render: (_, r) => <Text strong>{r.hostname}</Text> },
        { title: 'Label', dataIndex: 'label', key: 'label' },
        { title: 'Subdomains', key: 'sub', render: (_, r) => (r.includeSubdomains ? 'Included' : 'Exact host only') },
        { title: 'Added', key: 'added', render: (_, r) => `${formatDdMmYyyy(r.createdAt)}${r.createdBy ? ` by ${r.createdBy}` : ''}` },
        {
            title: 'Status',
            key: 'status',
            render: (_, r) => (r.active ? <Tag color="green">Approved</Tag> : <Tag>{`Removed ${formatDdMmYyyy(r.deactivatedAt)}`}</Tag>),
        },
        {
            title: '',
            key: 'actions',
            render: (_, r) => (r.active ? (
                <Popconfirm
                    title={`Remove ${r.hostname}?`}
                    description="New AI runs will no longer be able to fetch from this host. Drafts already created keep their source record."
                    okText="Remove host"
                    onConfirm={() => remove(r)}
                >
                    <Button size="small" danger>Remove</Button>
                </Popconfirm>
            ) : null),
        },
    ];

    return (
        <div>
            <Alert
                type="info"
                style={{ marginBottom: 12 }}
                message="Only hosts on this list can be fetched."
                description="Add regulator websites you trust as sources (for example a ministry or standards-body site). Plain DNS names only: no scheme, path, port, wildcard, IP address or localhost."
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <Input aria-label="Host name" placeholder="www.meity.gov.in" style={{ width: 240 }} value={hostname} onChange={(e) => setHostname(e.target.value)} />
                <Input aria-label="Label" placeholder="Label, e.g. MeitY" style={{ width: 220 }} value={label} onChange={(e) => setLabel(e.target.value)} />
                <Checkbox checked={includeSubdomains} onChange={(e) => setIncludeSubdomains(e.target.checked)}>Include subdomains</Checkbox>
                <Button onClick={add} loading={busy}>Add host</Button>
            </div>
            {formError && <Alert type="error" message={formError} data-testid="source-domain-error" style={{ marginBottom: 12 }} />}

            {domains.status === 'loading' && !domains.data && <Spin />}
            {domains.status === 'error' && (
                <Alert type="error" message={userMessageForError(domains.error)} action={<Button size="small" onClick={domains.reload}>Retry</Button>} />
            )}
            {domains.data && domains.data.length === 0 && (
                <Empty description="No approved source hosts yet. Add one above to fetch from URLs, or paste text when drafting." />
            )}
            {domains.data && domains.data.length > 0 && (
                <Table size="small" rowKey={(r) => r.id} columns={columns} dataSource={domains.data} pagination={false} />
            )}
        </div>
    );
};

export default SourceDomainManager;
